import type {
  CrawlJob,
  CrawlJobStatusDTO,
  CrawlSchedule,
  CrawlStatus,
  WithResourceId,
} from '../../types/knowledge';
import type { DirectoryUser } from '../../types/user';
import type { I18nContextValue } from '../../types/i18nContext';
import { formatDirectoryUserDisplayName } from '../user/userDisplay';
import { formatBytes, formatTimestamp } from '../../utils/formatUtils';
import { isAbandonedCrawl } from './knowledgePolling';
import {
  CRAWL_PAGE_DONE_STATUSES,
  CRAWL_PAGE_STATUSES,
  CRAWL_STAGING_DOCUMENT_DONE_STATUSES,
  CRAWL_STAGING_DOCUMENT_STATUSES,
  getCrawlProgressSnapshot,
} from './crawlProgress';
import {
  appendCrawlPhaseDurationDetailLines,
  appendMappedUrlCountDetailLine,
  appendStatusCountSection,
  CRAWL_PAGE_STATUS_KEYS,
  STAGING_DOCUMENT_STATUS_KEYS,
  type CrawlDetailLine,
} from './crawlDetailLineBuilders';
import { formatScheduleInterval } from './crawlMetaFormatters';

type Translate = I18nContextValue['translate'];

function appendCrawlScheduleDetailLines(
  lines: CrawlDetailLine[],
  schedule: WithResourceId<CrawlSchedule> | undefined,
  options: { translate: Translate; locale: string },
) {
  const { translate, locale } = options;

  if (schedule) {
    lines.push({
      text: translate('lac.websiteCrawls.detail.hasScheduleYes'),
    });
    lines.push({
      text:
        schedule.active === true
          ? translate('knowledge.crawlScheduleMetaActive', {
              interval: formatScheduleInterval(schedule, translate),
            })
          : schedule.active === false
            ? translate('knowledge.crawlScheduleMetaPaused')
            : translate('lac.websiteCrawls.detail.scheduleInterval', {
                interval: formatScheduleInterval(schedule, translate),
              }),
      indent: true,
    });
    if (schedule.lastRunAt) {
      lines.push({
        text: translate('lac.websiteCrawls.detail.scheduleLastRun', {
          date: formatTimestamp(schedule.lastRunAt, locale),
        }),
        indent: true,
      });
    }
    if (schedule.nextRunAt) {
      lines.push({
        text: translate('lac.websiteCrawls.detail.scheduleNextRun', {
          date: formatTimestamp(schedule.nextRunAt, locale),
        }),
        indent: true,
      });
    }
    return;
  }

  lines.push({
    text: translate('lac.websiteCrawls.detail.hasScheduleNo'),
  });
}

export function buildCrawlDetailLines(
  crawl: WithResourceId<CrawlJob>,
  liveStatus: CrawlJobStatusDTO | null | undefined,
  options: {
    translate: Translate;
    locale: string;
    status: CrawlStatus;
    schedule?: WithResourceId<CrawlSchedule>;
    directoryUsersById: Map<string, DirectoryUser>;
  },
): CrawlDetailLine[] {
  const { translate, locale, status, schedule, directoryUsersById } = options;
  const lines: CrawlDetailLine[] = [];

  const maxDepth = liveStatus?.maxDepth ?? crawl.maxDepth;
  if (maxDepth != null) {
    lines.push({
      text: translate('knowledge.crawlMetaDepth', { value: String(maxDepth) }),
    });
  }

  const maxPages = liveStatus?.maxPages ?? crawl.maxPages;
  if (maxPages != null) {
    lines.push({
      text: translate('knowledge.crawlMetaMaxPages', {
        value: String(maxPages),
      }),
    });
  }

  const useStartUrlAsPrefixFilter = crawl.useStartUrlAsPrefixFilter;
  if (useStartUrlAsPrefixFilter != null) {
    lines.push({
      text: translate('knowledge.crawlMetaUseStartUrlAsPrefixFilter', {
        value: translate(
          useStartUrlAsPrefixFilter
            ? 'knowledge.crawlMetaBooleanYes'
            : 'knowledge.crawlMetaBooleanNo',
        ),
      }),
    });
  }

  const allowSubdomains = crawl.allowSubdomains;
  if (allowSubdomains != null) {
    lines.push({
      text: translate('knowledge.crawlMetaAllowSubdomains', {
        value: translate(
          allowSubdomains
            ? 'knowledge.crawlMetaBooleanYes'
            : 'knowledge.crawlMetaBooleanNo',
        ),
      }),
    });
  }

  const excludedUrls = crawl.excludedUrls?.filter(
    (url): url is string => typeof url === 'string' && url.trim() !== '',
  );
  if (excludedUrls != null && excludedUrls.length > 0) {
    lines.push({
      text: translate('knowledge.crawlMetaExcludedUrls'),
    });
    for (const url of excludedUrls) {
      lines.push({ text: url, indent: true });
    }
  }

  appendMappedUrlCountDetailLine(
    lines,
    liveStatus?.mappedUrlCount ?? crawl.mappedUrlCount,
    {
      translate,
      locale,
      selectedUrlCount: crawl.selectedUrlCount,
    },
  );

  appendCrawlPhaseDurationDetailLines(
    lines,
    {
      mappingDuration: liveStatus?.mappingDuration ?? crawl.mappingDuration,
      crawlingDuration: liveStatus?.crawlingDuration ?? crawl.crawlingDuration,
      embeddingDuration:
        liveStatus?.embeddingDuration ?? crawl.embeddingDuration,
    },
    { translate, locale },
  );

  if (crawl.websiteName) {
    lines.push({
      text: translate('knowledge.crawlStatWebsiteName', {
        name: crawl.websiteName,
      }),
    });
  }

  if (crawl.totalSizeBytes != null) {
    lines.push({
      text: translate('knowledge.crawlStatTotalSize', {
        size: formatBytes(crawl.totalSizeBytes, locale),
      }),
    });
  }

  const startedByName =
    liveStatus?.startedByFirstName || liveStatus?.startedByLastName
      ? [liveStatus.startedByFirstName, liveStatus.startedByLastName]
          .filter(Boolean)
          .join(' ')
      : (liveStatus?.startedByUsername ??
        (crawl.startedBy
          ? formatDirectoryUserDisplayName(
              directoryUsersById.get(crawl.startedBy),
              translate('knowledge.members.unknownUser'),
            )
          : null));

  const startedAt = liveStatus?.startedAt ?? crawl.startedAt;
  if (startedByName && startedAt) {
    lines.push({
      text: translate('knowledge.crawlStatStartedByAt', {
        name: startedByName,
        date: formatTimestamp(startedAt, locale),
      }),
    });
  } else if (startedByName) {
    lines.push({
      text: translate('knowledge.crawlMetaStartedBy', { name: startedByName }),
    });
  } else if (startedAt) {
    lines.push({
      text: translate('knowledge.crawlStatStartedAt', {
        date: formatTimestamp(startedAt, locale),
      }),
    });
  }

  if (status === 'COMPLETED') {
    const lastSuccessfulRunAt =
      crawl.completedAt ?? schedule?.lastRunAt ?? null;
    if (lastSuccessfulRunAt) {
      lines.push({
        text: translate('knowledge.crawlStatLastSuccessfulRun', {
          date: formatTimestamp(lastSuccessfulRunAt, locale),
        }),
      });
    }
  }

  const progress = getCrawlProgressSnapshot(status, liveStatus);
  if (progress && (status === 'MAPPING' || status === 'AWAITING_SELECTION')) {
    const progressLabel = progress.labelParams
      ? translate(
          progress.labelKey as Parameters<typeof translate>[0],
          progress.labelParams,
        )
      : translate(progress.labelKey as Parameters<typeof translate>[0]);

    lines.push({
      text:
        progress.percentage != null
          ? translate('knowledge.crawlStatProgressWithPercent', {
              label: progressLabel,
              percent: String(progress.percentage),
            })
          : progressLabel,
    });
  }

  if (isAbandonedCrawl(crawl, liveStatus)) {
    lines.push({ text: translate('knowledge.crawlDetailAbandoned') });
  }

  appendCrawlScheduleDetailLines(lines, schedule, { translate, locale });

  appendStatusCountSection(lines, liveStatus?.pageStatusCounts, {
    translate,
    headerKey: 'knowledge.crawlDetailPageStatus',
    statusKeys: CRAWL_PAGE_STATUS_KEYS,
    statuses: CRAWL_PAGE_STATUSES,
    doneStatuses: CRAWL_PAGE_DONE_STATUSES,
  });

  appendStatusCountSection(lines, liveStatus?.stagingDocumentStatusCounts, {
    translate,
    headerKey: 'lac.websiteCrawls.detail.stagingDocumentStatus',
    statusKeys: STAGING_DOCUMENT_STATUS_KEYS,
    statuses: CRAWL_STAGING_DOCUMENT_STATUSES,
    doneStatuses: CRAWL_STAGING_DOCUMENT_DONE_STATUSES,
  });

  appendStatusCountSection(lines, liveStatus?.publishedDocumentStatusCounts, {
    translate,
    headerKey: 'lac.websiteCrawls.detail.publishedDocumentStatus',
    statusKeys: STAGING_DOCUMENT_STATUS_KEYS,
    statuses: CRAWL_STAGING_DOCUMENT_STATUSES,
    doneStatuses: CRAWL_STAGING_DOCUMENT_DONE_STATUSES,
    includeStatusBreakdown: false,
  });

  const errorMessage = liveStatus?.errorMessage ?? crawl.errorMessage;
  if (errorMessage) {
    lines.push({
      text: translate('knowledge.crawlStatError', { message: errorMessage }),
    });
  }

  return lines;
}
